import React, { useEffect, useMemo, useState } from "react";
import { CometChatUIKit } from "@cometchat/chat-uikit-react";
import { workerGetModels, workerImpersonate } from "./api";
import { getSession, setSession, clearSession } from "./storage";

type ModelRow = {
  uid: string;
  display_name?: string | null;
  avatar_url?: string | null;
};

function merge(...objs: Array<React.CSSProperties | false | null | undefined>): React.CSSProperties {
  const out: React.CSSProperties = {};
  for (const o of objs) {
    if (!o) continue;
    Object.assign(out, o);
  }
  return out;
}

export default function AdminPage() {
  const [adminKey, setAdminKeyState] = useState<string>(() => getSession("tff_admin_key") || "");
  const [showKey, setShowKey] = useState(false);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<string>("");
  const [models, setModels] = useState<ModelRow[]>([]);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<string>(() => getSession("tff_last_model_uid") || "");
  const [loadedOnce, setLoadedOnce] = useState(false);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return models;

    return models.filter(m => {
      const name = (m.display_name || "").toLowerCase();
      const uid = (m.uid || "").toLowerCase();
      return name.includes(q) || uid.includes(q);
    });
  }, [models, query]);

  async function loadModels(refresh: boolean) {
    if (!adminKey.trim()) {
      setStatus("Bitte Admin Key eingeben.");
      return;
    }

    setBusy(true);
    setStatus(refresh ? "Models werden aktualisiert." : "Models werden geladen.");

    try {
      const res = await workerGetModels(adminKey.trim(), refresh);
      const list = Array.isArray(res?.models) ? (res.models as ModelRow[]) : [];
      setModels(list);
      setLoadedOnce(true);
      setStatus(list.length ? `Bereit. ${list.length} Models geladen.` : "Keine Models gefunden.");
    } catch (e: any) {
      setStatus(`Fehler beim Laden. ${String(e?.message || e)}`);
    } finally {
      setBusy(false);
    }
  }

  async function switchToModel(uid: string) {
    if (!adminKey.trim()) {
      setStatus("Bitte Admin Key eingeben.");
      return;
    }

    setBusy(true);
    setStatus(`Wechsel zu ${uid}.`);

    try {
      setSession("tff_admin_key", adminKey.trim());
      setSession("tff_last_model_uid", uid);

      const res = await workerImpersonate(adminKey.trim(), uid);
      const authToken = String(res?.authToken || "");
      if (!authToken) throw new Error("Kein authToken vom Worker erhalten.");

      try {
        const me = await CometChatUIKit.getLoggedinUser();
        if (me) await CometChatUIKit.logout();
      } catch {}

      await CometChatUIKit.loginWithAuthToken(authToken);

      setSession("tff_mode", "model");
      setSession("tff_model_uid", uid);

      window.location.href = "/";
    } catch (e: any) {
      setStatus(`Fehler beim Wechsel. ${String(e?.message || e)}`);
    } finally {
      setBusy(false);
    }
  }

  async function logoutEverywhere() {
    setBusy(true);
    setStatus("Logout läuft.");

    try {
      try {
        const me = await CometChatUIKit.getLoggedinUser();
        if (me) await CometChatUIKit.logout();
      } catch {}

      clearSession("tff_mode");
      clearSession("tff_model_uid");

      setStatus("Ausgeloggt.");
    } finally {
      setBusy(false);
    }
  }

  function setAdminKey(val: string) {
    setAdminKeyState(val);
    const k = val.trim();
    if (k) setSession("tff_admin_key", k);
  }

  useEffect(() => {
    const k = adminKey.trim();
    if (k) setSession("tff_admin_key", k);

    if (k && !loadedOnce) {
      loadModels(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currentSelected = selected || (models[0]?.uid || "");

  return (
    <div style={styles.page}>
      <div style={styles.bgGlow} />

      <div style={styles.shell}>
        <div style={styles.header}>
          <div style={styles.brand}>
            <div style={styles.dot} />
            <div>
              <div style={styles.title}>Admin Panel</div>
              <div style={styles.subtitle}>Model Inbox auswählen und direkt in die Chat UI wechseln.</div>
            </div>
          </div>

          <div style={styles.headerActions}>
            <button
              type="button"
              onClick={() => loadModels(true)}
              disabled={busy || !adminKey.trim()}
              style={merge(styles.btn, styles.btnGhost, (busy || !adminKey.trim()) && styles.btnDisabled)}
            >
              Refresh
            </button>

            <button
              type="button"
              onClick={logoutEverywhere}
              disabled={busy}
              style={merge(styles.btn, styles.btnGhost, busy && styles.btnDisabled)}
            >
              Logout
            </button>
          </div>
        </div>

        <div style={styles.card}>
          <div style={styles.grid}>
            <div style={styles.field}>
              <label style={styles.label}>Admin Key</label>
              <div style={styles.inputRow}>
                <input
                  value={adminKey}
                  onChange={e => setAdminKey(e.target.value)}
                  type={showKey ? "text" : "password"}
                  placeholder="Authorization Bearer ADMIN_KEY"
                  style={styles.input}
                  autoComplete="off"
                />
                <button
                  type="button"
                  onClick={() => setShowKey(v => !v)}
                  style={merge(styles.btn, styles.btnGhost)}
                >
                  {showKey ? "Hide" : "Show"}
                </button>
                <button
                  type="button"
                  onClick={() => loadModels(false)}
                  disabled={busy || !adminKey.trim()}
                  style={merge(styles.btn, styles.btnPrimary, (busy || !adminKey.trim()) && styles.btnDisabled)}
                >
                  Connect
                </button>
              </div>
              <div style={styles.hint}>
                Der Key wird nur im Storage gespeichert. Admin Endpoints akzeptieren nur Authorization Bearer.
              </div>
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Suche</label>
              <input
                value={query}
                onChange={e => setQuery(e.target.value)}
                type="text"
                placeholder="Name oder UID"
                style={styles.input}
              />
              <div style={styles.hint}>Tipp, nutze model_001 oder den Anzeigenamen wie Quiana.</div>
            </div>
          </div>

          {status ? <div style={styles.status}>{status}</div> : null}
        </div>

        <div style={styles.split}>
          <div style={styles.left}>
            <div style={styles.sectionTitle}>Models</div>

            <div style={styles.list}>
              {filtered.map(m => {
                const name = m.display_name || m.uid;
                const active = currentSelected === m.uid;

                return (
                  <button
                    key={m.uid}
                    type="button"
                    onClick={() => {
                      setSelected(m.uid);
                      setSession("tff_last_model_uid", m.uid);
                    }}
                    style={merge(styles.row, active && styles.rowActive)}
                  >
                    <div style={styles.avatarWrap}>
                      {m.avatar_url ? (
                        <img src={m.avatar_url} alt={name} style={styles.avatarImg} />
                      ) : (
                        <div style={styles.avatarFallback}>{name.slice(0, 2).toUpperCase()}</div>
                      )}
                    </div>

                    <div style={styles.rowText}>
                      <div style={styles.rowName}>{name}</div>
                      <div style={styles.rowUid}>{m.uid}</div>
                    </div>

                    <div style={styles.rowRight}>
                      <div style={styles.pill}>{active ? "Selected" : "Select"}</div>
                    </div>
                  </button>
                );
              })}

              {!busy && loadedOnce && filtered.length === 0 ? (
                <div style={styles.empty}>Keine Models gefunden.</div>
              ) : null}
            </div>
          </div>

          <div style={styles.right}>
            <div style={styles.sectionTitle}>Aktion</div>

            <div style={styles.actionCard}>
              <div style={styles.actionTop}>
                <div style={styles.actionLabel}>Ausgewähltes Model</div>
                <div style={styles.actionValue}>{currentSelected || "Keins"}</div>
              </div>

              <button
                type="button"
                onClick={() => currentSelected && switchToModel(currentSelected)}
                disabled={busy || !currentSelected || !adminKey.trim()}
                style={merge(styles.btn, styles.btnPrimary, styles.btnWide, (busy || !currentSelected || !adminKey.trim()) && styles.btnDisabled)}
              >
                Inbox öffnen
              </button>

              <div style={styles.actionHint}>
                Danach landest du direkt in der normalen Chat UI und siehst die Inbox dieses Models.
              </div>
            </div>

            <div style={styles.note}>
              <div style={styles.noteTitle}>Hinweis</div>
              <div style={styles.noteText}>
                Wenn ein Kunde Quiana nicht sieht, fehlt meist die erste Conversation. Der Worker kann beim Kauf eine Begrüßung senden, dann ist es sofort gelöst.
              </div>
            </div>
          </div>
        </div>

        <div style={styles.footer}>
          <span style={styles.footerMuted}>Worker</span>
          <span style={styles.footerValue}>{String(process.env.REACT_APP_WORKER_URL || "")}</span>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: "100vh",
    background:
      "radial-gradient(1200px 600px at 10% 10%, rgba(250,160,187,0.12), transparent 60%), radial-gradient(900px 500px at 90% 20%, rgba(250,160,187,0.08), transparent 55%), #0b0b0f",
    color: "rgba(255,255,255,0.92)",
    padding: 24
  },
  bgGlow: {
    position: "fixed",
    inset: 0,
    pointerEvents: "none",
    background: "radial-gradient(800px 500px at 60% 80%, rgba(250,160,187,0.08), transparent 55%)"
  },
  shell: {
    maxWidth: 1100,
    margin: "0 auto"
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
    marginBottom: 16
  },
  brand: {
    display: "flex",
    alignItems: "center",
    gap: 12
  },
  dot: {
    width: 14,
    height: 14,
    borderRadius: 999,
    background: "rgba(250,160,187,0.9)",
    boxShadow: "0 0 0 6px rgba(250,160,187,0.12)"
  },
  title: {
    fontSize: 22,
    fontWeight: 800,
    letterSpacing: 0.2
  },
  subtitle: {
    fontSize: 13,
    color: "rgba(255,255,255,0.65)",
    marginTop: 3
  },
  headerActions: {
    display: "flex",
    gap: 10,
    alignItems: "center"
  },
  card: {
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 16,
    padding: 16,
    boxShadow: "0 10px 30px rgba(0,0,0,0.35)",
    backdropFilter: "blur(10px)",
    marginBottom: 16
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "1.2fr 1fr",
    gap: 16
  },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: 8
  },
  label: {
    fontSize: 12,
    color: "rgba(255,255,255,0.7)"
  },
  inputRow: {
    display: "flex",
    gap: 10,
    alignItems: "center"
  },
  input: {
    width: "100%",
    background: "rgba(0,0,0,0.35)",
    border: "1px solid rgba(255,255,255,0.10)",
    color: "rgba(255,255,255,0.92)",
    padding: "12px 12px",
    borderRadius: 12,
    outline: "none"
  },
  hint: {
    fontSize: 12,
    color: "rgba(255,255,255,0.55)"
  },
  status: {
    marginTop: 12,
    fontSize: 13,
    color: "rgba(255,255,255,0.75)"
  },
  split: {
    display: "grid",
    gridTemplateColumns: "1.6fr 1fr",
    gap: 16
  },
  left: {
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.07)",
    borderRadius: 16,
    padding: 16
  },
  right: {
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.07)",
    borderRadius: 16,
    padding: 16
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: 800,
    color: "rgba(255,255,255,0.75)",
    marginBottom: 12,
    letterSpacing: 0.2
  },
  list: {
    display: "flex",
    flexDirection: "column",
    gap: 10
  },
  row: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    textAlign: "left",
    width: "100%",
    padding: 12,
    borderRadius: 14,
    background: "rgba(0,0,0,0.25)",
    border: "1px solid rgba(255,255,255,0.08)",
    cursor: "pointer"
  },
  rowActive: {
    border: "1px solid rgba(250,160,187,0.45)",
    boxShadow: "0 0 0 4px rgba(250,160,187,0.10)"
  },
  avatarWrap: {
    width: 44,
    height: 44,
    borderRadius: 14,
    overflow: "hidden",
    flex: "0 0 auto",
    background: "rgba(250,160,187,0.10)",
    border: "1px solid rgba(250,160,187,0.20)"
  },
  avatarImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover"
  },
  avatarFallback: {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 800,
    color: "rgba(255,255,255,0.9)"
  },
  rowText: {
    flex: "1 1 auto",
    minWidth: 0
  },
  rowName: {
    fontSize: 14,
    fontWeight: 800,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  },
  rowUid: {
    fontSize: 12,
    color: "rgba(255,255,255,0.55)",
    marginTop: 2
  },
  rowRight: {
    flex: "0 0 auto"
  },
  pill: {
    fontSize: 12,
    padding: "6px 10px",
    borderRadius: 999,
    background: "rgba(255,255,255,0.06)",
    border: "1px solid rgba(255,255,255,0.10)",
    color: "rgba(255,255,255,0.75)"
  },
  empty: {
    padding: 16,
    color: "rgba(255,255,255,0.6)",
    fontSize: 13
  },
  actionCard: {
    background: "rgba(0,0,0,0.25)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 16,
    padding: 16
  },
  actionTop: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    marginBottom: 12
  },
  actionLabel: {
    fontSize: 12,
    color: "rgba(255,255,255,0.60)"
  },
  actionValue: {
    fontSize: 16,
    fontWeight: 900
  },
  actionHint: {
    marginTop: 12,
    fontSize: 12,
    color: "rgba(255,255,255,0.55)",
    lineHeight: 1.4
  },
  note: {
    marginTop: 12,
    padding: 14,
    borderRadius: 16,
    background: "rgba(250,160,187,0.07)",
    border: "1px solid rgba(250,160,187,0.16)"
  },
  noteTitle: {
    fontSize: 12,
    fontWeight: 900,
    marginBottom: 6
  },
  noteText: {
    fontSize: 12,
    color: "rgba(255,255,255,0.70)",
    lineHeight: 1.45
  },
  footer: {
    marginTop: 14,
    display: "flex",
    gap: 10,
    alignItems: "center",
    fontSize: 12,
    color: "rgba(255,255,255,0.6)"
  },
  footerMuted: {
    opacity: 0.7
  },
  footerValue: {
    opacity: 0.9
  },
  btn: {
    borderRadius: 12,
    padding: "12px 14px",
    border: "1px solid rgba(255,255,255,0.10)",
    background: "rgba(255,255,255,0.06)",
    color: "rgba(255,255,255,0.9)",
    cursor: "pointer",
    fontWeight: 800
  },
  btnGhost: {
    background: "rgba(255,255,255,0.04)"
  },
  btnPrimary: {
    background: "linear-gradient(135deg, rgba(250,160,187,0.95), rgba(247,139,168,0.95))",
    border: "1px solid rgba(0,0,0,0.15)",
    color: "rgba(10,10,12,0.95)"
  },
  btnWide: {
    width: "100%"
  },
  btnDisabled: {
    opacity: 0.55,
    cursor: "not-allowed"
  }
};
